// src/pages/ManualMarketing/ManualMarketingAI/ManualMarketingAIRegen.jsx
import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Step2 from "./Step2";

export default function ManualMarketingAIRegen() {
    const location = useLocation();
    const navigate = useNavigate();

    // ✅ debug 토글 (콘솔에서 localStorage.setItem("__DEBUG_REGEN__", "true"))
    const DEBUG = useMemo(() => {
        try {
            return localStorage.getItem("__DEBUG_REGEN__") === "true";
        } catch {
            return false;
        }
    }, []);

    // ✅ location.state는 렌더마다 객체 참조가 바뀔 수 있어 memo로 고정 (lint 해결)
    const rawState = useMemo(() => location.state || {}, [location.state]);

    // ✅ result -> regen 포맷: { isRegen, data:{...} } 우선
    const initialIsRegen = useMemo(
        () => Boolean(rawState?.isRegen),
        [rawState]
    );

    const initialData = useMemo(() => {
        return rawState?.data ?? rawState ?? {};
    }, [rawState]);

    const original = useMemo(() => rawState?.data ?? rawState ?? {}, [rawState]);

    // ✅ 기존에 쓰던 옵션 맵(코드 -> 라벨)
    const titleOptions = useMemo(
        () => ({
            1: "매장 홍보",
            2: "상품 소개",
            3: "이벤트",
            4: "감사 인사",
        }),
        []
    );

    const channelOptions = useMemo(
        () => ({
            1: "카카오톡",
            2: "인스타 스토리",
            3: "인스타 게시물",
            4: "블로그",
            5: "MMS(문자)",
            6: "네이버 밴드",
            7: "X(트위터)",
        }),
        []
    );

    const ageOptions = useMemo(
        () => ({
            1: "10대",
            2: "20대",
            3: "30대",
            4: "40대",
            5: "50대",
            6: "60대 이상",
        }),
        []
    );

    // "인스타 스토리" -> { channel:"인스타그램", subChannel:"스토리" }
    const fromSheetChannelValue = useCallback((selected) => {
        const s = String(selected || "").trim();
        if (!s) return { channel: "", subChannel: "" };

        if (s === "카카오톡") return { channel: "카카오톡", subChannel: "" };
        if (s === "네이버 밴드" || s === "네이버밴드") {
            return { channel: "네이버 밴드", subChannel: "" };
        }

        if (s.startsWith("인스타")) {
            return {
                channel: "인스타그램",
                subChannel: s.includes("스토리") ? "스토리" : "게시물",
            };
        }

        return { channel: s, subChannel: "" };
    }, []);

    // ✅ age: 코드/라벨 무엇이 와도 라벨로 통일
    const normalizeAgeLabel = useCallback(
        (ageLike) => {
            if (ageLike == null) return "";

            const v = String(ageLike).trim();

            // 이미 라벨이면 그대로
            if (Object.values(ageOptions).includes(v)) return v;

            // 코드면 라벨로
            if (ageOptions[v]) return ageOptions[v];

            return v; // 혹시 이미 "30대" 같은 변형이 오면 그대로 두기
        },
        [ageOptions]
    );

    // ✅ theme: theme/ad_text_theme/title(코드) 어떤 게 와도 "테마 라벨"로 통일
    const normalizeThemeLabel = useCallback(
        (data) => {
            const theme = String(data?.theme ?? "").trim();
            if (theme) return theme;

            const adTextTheme = String(data?.ad_text_theme ?? "").trim();
            if (adTextTheme) return adTextTheme;

            const titleCode = String(data?.title ?? "").trim();
            if (titleOptions[titleCode]) return titleOptions[titleCode];

            return "";
        },
        [titleOptions]
    );

    // ✅ channel: 코드/시트라벨/베이스채널 어떤 게 와도 Step2가 쓰는 {channel, subChannel}로 통일
    const normalizeChannelAndSub = useCallback(
        (data) => {
            const rawChannel = String(data?.channel ?? "").trim();
            const rawSub = String(data?.subChannel ?? "").trim();

            // 1) channel이 코드면 -> 시트라벨 -> 변환
            if (/^\d+$/.test(rawChannel)) {
                const sheet = channelOptions[rawChannel] || "";
                const parsed = fromSheetChannelValue(sheet);
                return {
                    channel: parsed.channel || "",
                    subChannel: parsed.subChannel || "",
                };
            }

            // 2) channel이 "인스타 스토리" 같은 시트 라벨이면 변환
            const parsed = fromSheetChannelValue(rawChannel);
            const nextChannel = parsed.channel || rawChannel || "";

            // 3) subChannel은 명시값 우선
            const nextSub = rawSub || parsed.subChannel || "";

            return { channel: nextChannel, subChannel: nextSub };
        },
        [channelOptions, fromSheetChannelValue]
    );

    // ✅ Step2에 넘길 form을 "한 번만" 정규화해서 만든다
    const normalizedInitial = useMemo(() => {
        const { channel, subChannel } = normalizeChannelAndSub(initialData);

        return {
            ...initialData,

            // Step2 화면 표시용(라벨/구조)로 통일
            age: normalizeAgeLabel(initialData?.age),
            channel,
            subChannel,
            theme: normalizeThemeLabel(initialData),

            // style은 Step2가 문자열로 쓰는 경우가 많아서 문자열로 정리
            style: initialData?.style != null ? String(initialData.style) : "",

            // regen 확정
            isRegen: initialIsRegen || Boolean(initialData?.isRegen),
        };
    }, [
        initialData,
        initialIsRegen,
        normalizeAgeLabel,
        normalizeThemeLabel,
        normalizeChannelAndSub,
    ]);

    const [form, setForm] = useState(() => normalizedInitial);

    // ✅ Step2가 쓰는 updateData
    const updateData = useCallback((patch) => {
        setForm((prev) => ({ ...prev, ...(patch || {}) }));
    }, []);

    // ✅ Step2 -> loading : loading이 잘 먹게 flat + data 같이 보냄
    const handleNext = useCallback(
        (patchFromStep2) => {
            const merged = { ...form, ...(patchFromStep2 || {}) };

            const nextState = {
                ...merged,
                isRegen: true,

                // loading이 기대하는 키들(기존 로직 유지)
                store_business_number: merged.store_business_number,
                age: merged.age, // ✅ 라벨로 통일된 값
                channel: merged.channel, // ✅ "인스타그램"/"카카오톡" 등
                subChannel: merged.subChannel, // ✅ "스토리"/"게시물" 등
                theme: merged.theme, // ✅ "이벤트" 등
                style: merged.style, // ✅ "6" 등

                // 로딩에서 참고 가능하게 원본도 같이
                data: { ...merged, isRegen: true },
            };

            navigate("/manual/marketing/ai/loading", { state: nextState });
        },
        [form, navigate]
    );

    // ✅ 뒤로가기: 편집값 들고 result로 복귀
    const handleBack = useCallback(() => {
        const merged = { ...original, ...form, isRegen: true };

        const backState = {
            ...merged,
            isRegen: true,
            data: merged,
        };

        if (DEBUG) {
            console.groupCollapsed("[AIRegen] -> result(back)");
            console.log("backState =", backState);
            console.groupEnd();
        }

        navigate("/manual/marketing/ai/result", {
            state: backState,
            replace: true,
        });
    }, [navigate, form, DEBUG, original]);

    return (
        <Step2
            data={form}
            updateData={updateData}
            onBack={handleBack}
            onNext={handleNext}
            isRegen={true}
        />
    );
}
